"use client";

import { Heading, Text } from "@pamperedchef/design-system";
import { useLocale } from "next-intl";
import { useCallback } from "react";
import type { LocaleState } from "@/app/store";
import { analyticsManager } from "@/app/utilities/analytics/analytics-manager";
import type { BlockOf } from "@/domain/catalog";
import { SeeInActionPanel } from "@/composites/foundation/SeeInActionPanel";
import { extractWidenAssetId } from "../../helpers/extract-widen-asset-id";
import * as styles from "./SeeInAction.module.scss";

export function SeeInAction({
	block,
	parentSku
}: {
	block: BlockOf<"see_in_action_block">;
	parentSku?: string;
}) {
	const locale = useLocale() as LocaleState;
	const videoWidenId = block.video_widen_id;

	// "See it in action" sits outside the media gallery, so the event has no
	// gallery slot — `position` is intentionally omitted.
	const trackVideoClick = useCallback(() => {
		if (!parentSku) return;
		analyticsManager.track({
			type: "pdp_content_click",
			actionName: "video_click",
			sku: parentSku,
			locale,
			assetId: extractWidenAssetId(videoWidenId)
		});
	}, [parentSku, locale, videoWidenId]);

	if (!block.video_widen_id) return null;
	return (
		<section data-block="see_in_action_block" className={styles["section"]}>
			<Heading tag="h2" className={styles["heading"]}>
				{block.headline}
			</Heading>
			{block.body ? (
				<Text tag="p" styledAs="mdr" className={styles["deck"]}>
					{block.body}
				</Text>
			) : null}
			<SeeInActionPanel
				videoUrl={block.video_widen_id}
				posterUrl={block.poster_widen_id}
				ariaLabel={block.headline}
				onPlay={trackVideoClick}
			/>
		</section>
	);
}
